import type { AccusationPhaseState } from "@cold-case/shared/accusation-phase";

import { voteModalBody } from "../content/accusation-phase-copy.js";
import { mergeAccusationPhaseFromSync } from "./accusation-phase-merge.js";
import { setPlayerOverlayOpen } from "../lib/player-input-gate.js";

function el(id: string): HTMLElement {
  const node = document.getElementById(id);
  if (!node) throw new Error(`#${id} missing`);
  return node;
}

function remainingVoteSeconds(phase: AccusationPhaseState): number {
  return Math.max(0, Math.ceil((phase.voteDeadlineMs - Date.now()) / 1000));
}

export type AccusationVoteModalHandlers = {
  onVote: (vote: "yes" | "no") => void;
};

let timerId: ReturnType<typeof setInterval> | null = null;
let currentPhase: AccusationPhaseState | null = null;
let selfUserId = "";
let voteHandlers: AccusationVoteModalHandlers | null = null;
let votePlayersCache: { userId: string; displayName: string }[] = [];

function stopTimer(): void {
  if (timerId) {
    clearInterval(timerId);
    timerId = null;
  }
}

function updateTimerDisplay(): void {
  const footer = document.getElementById("accusation-vote-timer");
  if (!footer || !currentPhase) return;
  const sec = remainingVoteSeconds(currentPhase);
  footer.textContent = `${sec}...`;
}

function renderVoteStatus(phase: AccusationPhaseState, players: { userId: string; displayName: string }[]): void {
  const statusEl = document.getElementById("accusation-vote-status");
  if (!statusEl) return;
  const lines = players.map((p) => {
    const v = phase.votes[p.userId] ?? "pending";
    const label =
      v === "yes" ? "Yes" : v === "no" ? "No" : "Waiting…";
    return `<span>${escapeHtml(p.displayName)}: <strong>${label}</strong></span>`;
  });
  statusEl.innerHTML = lines.join(" · ");
}

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function applyVoteUi(phase: AccusationPhaseState): void {
  const myVote = phase.votes[selfUserId];
  const yesBtn = document.getElementById("accusation-vote-yes") as HTMLButtonElement | null;
  const noBtn = document.getElementById("accusation-vote-no") as HTMLButtonElement | null;
  const voted = myVote === "yes" || myVote === "no";
  if (yesBtn) {
    yesBtn.disabled = voted;
    yesBtn.textContent = myVote === "yes" ? "You voted Yes" : "Yes";
  }
  if (noBtn) {
    noBtn.disabled = voted;
    noBtn.textContent = myVote === "no" ? "You voted No" : "No";
  }
}

export function openAccusationVoteModal(
  phase: AccusationPhaseState,
  proposerName: string,
  playerCount: number,
  userId: string,
  players: { userId: string; displayName: string }[],
  handlers: AccusationVoteModalHandlers
): void {
  currentPhase = phase;
  selfUserId = userId;
  voteHandlers = handlers;
  votePlayersCache = players;
  const overlay = el("accusation-vote-overlay");
  const body = el("accusation-vote-body");
  body.innerHTML = `${voteModalBody(proposerName, playerCount)}
<p id="accusation-vote-status" class="acc-vote-status" aria-live="polite"></p>`;

  const yesBtn = el("accusation-vote-yes");
  const noBtn = el("accusation-vote-no");
  const submitVote = (vote: "yes" | "no") => {
    if (!currentPhase || !selfUserId) return;
    if (currentPhase.votes[selfUserId] !== "pending") return;
    currentPhase.votes[selfUserId] = vote;
    applyVoteUi(currentPhase);
    renderVoteStatus(currentPhase, votePlayersCache);
    voteHandlers?.onVote(vote);
  };
  yesBtn.onclick = () => submitVote("yes");
  noBtn.onclick = () => submitVote("no");

  overlay.classList.add("open");
  overlay.setAttribute("aria-hidden", "false");
  setPlayerOverlayOpen("accusation-vote", true);
  renderVoteStatus(phase, players);
  applyVoteUi(phase);
  updateTimerDisplay();
  stopTimer();
  timerId = setInterval(updateTimerDisplay, 1000);
  requestAnimationFrame(() => yesBtn.focus());
}

export function syncAccusationVoteModal(
  phase: AccusationPhaseState,
  players: { userId: string; displayName: string }[]
): void {
  currentPhase = mergeAccusationPhaseFromSync(phase, currentPhase, selfUserId);
  votePlayersCache = players;
  if (!currentPhase) return;
  renderVoteStatus(currentPhase, players);
  applyVoteUi(currentPhase);
  updateTimerDisplay();
  if (remainingVoteSeconds(currentPhase) <= 0) {
    updateTimerDisplay();
  }
}

export function closeAccusationVoteModal(): void {
  stopTimer();
  currentPhase = null;
  selfUserId = "";
  voteHandlers = null;
  const overlay = document.getElementById("accusation-vote-overlay");
  overlay?.classList.remove("open");
  overlay?.setAttribute("aria-hidden", "true");
  setPlayerOverlayOpen("accusation-vote", false);
}

export function isAccusationVoteModalOpen(): boolean {
  return document.getElementById("accusation-vote-overlay")?.classList.contains("open") ?? false;
}
